import debounce from 'lodash/debounce'
import React, { useEffect, useMemo, useRef, useState } from 'react'

import { Switch } from '@thanx/uikit/switch'
import NoSearchResultsFound from 'components/NoSearchResultsFound'
import Pagination from 'components/Pagination'
import SearchInput from 'components/SearchInput'
import Spinner from 'components/Spinner'

type PropsT = {
  children: React.ReactNode
  contentIsLoading: boolean
  onChangePage: (page: number) => unknown
  onChangeSearchInput: (value: string) => void
  currentPage: number
  numberOfPages: number
  searchPlaceholder?: string
  // Seeds the search box, e.g. from a query persisted in the URL, so
  // navigating back to this content after leaving it doesn't clear a
  // search the user already typed.
  initialSearchValue?: string
}

const SearchablePaginatedContent = ({
  children,
  contentIsLoading,
  onChangePage,
  onChangeSearchInput,
  currentPage,
  numberOfPages,
  searchPlaceholder = '',
  initialSearchValue = '',
}: PropsT) => {
  const [searchValue, setSearchValue] = useState(initialSearchValue)

  // Routed through a ref rather than a useMemo dep, so an unmemoized
  // `onChangeSearchInput` doesn't spawn an orphaned debounce instance —
  // there's only ever one, and it always calls whichever callback is current.
  const onChangeSearchInputRef = useRef(onChangeSearchInput)
  onChangeSearchInputRef.current = onChangeSearchInput

  const notifySearchChange = useMemo(
    () =>
      debounce((value: string) => onChangeSearchInputRef.current(value), 500),
    []
  )

  // Cancel any pending debounced keystroke on unmount — without this, it
  // can still fire after the caller navigates away mid-debounce, e.g. on
  // the customer search page, writing the stale query onto whatever page's
  // URL is current by then and corrupting it with a leftover `?q=`.
  useEffect(() => notifySearchChange.cancel, [notifySearchChange])

  const handleSearchInputChange = (value: string) => {
    setSearchValue(value)
    notifySearchChange(value)
  }

  return (
    <>
      <SearchInput
        className={'w-100 mb-xl'}
        onChange={handleSearchInputChange}
        value={searchValue}
        placeholder={searchPlaceholder}
      />
      <Spinner
        className={'d-flex justify-content-center'}
        size={'3x'}
        isLoading={contentIsLoading}
      >
        <Switch
          condition={numberOfPages > 0}
          fallback={<NoSearchResultsFound />}
        >
          {children}
        </Switch>
      </Spinner>
      <Switch condition={!contentIsLoading && numberOfPages > 0}>
        <div className={'d-flex justify-content-center margin-top-huge'}>
          <Pagination
            currentPage={currentPage}
            onChangePage={onChangePage}
            numPages={numberOfPages}
          />
        </div>
      </Switch>
    </>
  )
}

export default SearchablePaginatedContent
